import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Modal, Input, Loader} from 'rsuite'
import {Link, ContentEditRoute} from '@dudagroup/editor'
import {useSearchEditorQuery} from '../../../api'

const SEARCH_DEBOUNCE_MS = 250
const PAGE_SIZE = 12

/**
 * Keyboard-only command palette (Ctrl/Cmd+Shift+K). Cross-content-type record search backed by
 * the `searchEditor` resolver; ↑/↓ to move, Enter to open, Esc to close. Deliberately has no
 * visible trigger — it is a power-user tool.
 *
 * Always-mounted via EditorConfig.globalComponents (so the hotkey works from any route). It
 * renders its own <Modal>, so it occupies no layout space until opened.
 */
export function CommandPalette() {
  const {t, i18n} = useTranslation()
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [term, setTerm] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Global hotkey. Ignores repeats and does not fire while typing elsewhere is irrelevant here
  // (the chord is unlikely to collide), but we still guard against the browser default.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'K' || e.key === 'k')) {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const onChange = (value: string) => {
    setInputValue(value)
    setActiveIndex(0)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setTerm(value.trim()), SEARCH_DEBOUNCE_MS)
  }

  const {data, loading} = useSearchEditorQuery({
    skip: !open || term.length === 0,
    fetchPolicy: 'no-cache',
    variables: {term, language: i18n.language, first: PAGE_SIZE},
  })

  const hits = useMemo(() => data?.extensions.searchEditor.hits ?? [], [data])

  const close = useCallback(() => {
    setOpen(false)
    setInputValue('')
    setTerm('')
    setActiveIndex(0)
  }, [])

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, hits.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && hits[activeIndex]) {
      const hit = hits[activeIndex]
      // Navigate by setting the location; Link handles SPA routing, but from a keydown we
      // resolve the same route target the row's Link uses.
      const target = ContentEditRoute.create({
        type: hit.contentType,
        id: hit.id,
        revision: String(hit.revision),
      })
      if (target?.path) {
        window.location.assign(target.path)
        close()
      }
    }
  }

  return (
    <Modal open={open} onClose={close} size="sm" backdrop overflow>
      <Modal.Body style={{padding: 0}} onKeyDown={onListKeyDown}>
        <Input
          value={inputValue}
          onChange={onChange}
          placeholder={t('commandPalette.placeholder', 'Springe zu … (Inhalt suchen)')}
          autoFocus
          style={{border: 'none', fontSize: 16, padding: 12}}
        />
        {loading && <Loader style={{margin: 12}} />}
        <div style={{maxHeight: 400, overflowY: 'auto'}}>
          {hits.map((hit, i) => (
            <Link
              key={`${hit.contentType}-${hit.id}-${hit.revision}`}
              route={ContentEditRoute.create({
                type: hit.contentType,
                id: hit.id,
                revision: String(hit.revision),
              })}
              onClick={close}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                background: i === activeIndex ? '#f2f7ff' : 'transparent',
                color: 'inherit',
                textDecoration: 'none',
              }}>
              <span style={{fontSize: 11, color: '#8e8e93', minWidth: 90}}>{hit.contentType}</span>
              <span>{hit.title || t('commandPalette.untitled', '(ohne Titel)')}</span>
            </Link>
          ))}
          {!loading && term.length > 0 && hits.length === 0 && (
            <div style={{padding: 12, color: '#8e8e93'}}>
              {t('commandPalette.empty', 'Nichts gefunden.')}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}
